import {
  UsersIcon,
  BookOpenIcon,
  StoreIcon,
  CalendarIcon,
  PackageIcon,
  TrendingDownIcon,
  HeartIcon,
  MessageSquareIcon,
  InfoIcon,
  StarIcon,
  CheckCircleIcon,
} from 'lucide-react'
import React from 'react'

export type NotificationType =
  | 'co_author_request'
  | 'arc_application'
  | 'collaboration_invite'
  | 'bookstore_stock'
  | 'event_invite'
  | 'order_shipped'
  | 'order_delivered'
  | 'new_release'
  | 'price_drop'
  | 'new_follower'
  | 'review_posted'
  | 'wishlist_update'
  | 'event_reminder'
  | 'system'

export type NotificationCategory =
  | 'approvals'
  | 'orders'
  | 'releases'
  | 'social'
  | 'events'
  | 'system'

export interface Notification {
  id: string
  type: NotificationType
  category: NotificationCategory
  title: string
  message: string
  time: string
  read: boolean
  actionable: boolean
  actions?: {
    approve?: string
    decline?: string
  }
  senderName?: string
  senderAvatar?: string
  relatedImage?: string
  relatedLink?: string
}

export const notificationCategoryLabels: Record<NotificationCategory, string> =
  {
    approvals: 'Approvals',
    orders: 'Orders',
    releases: 'Releases',
    social: 'Social',
    events: 'Events',
    system: 'System',
  }

export const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'co_author_request':
    case 'collaboration_invite':
    case 'new_follower':
      return UsersIcon
    case 'arc_application':
    case 'new_release':
      return BookOpenIcon
    case 'bookstore_stock':
      return StoreIcon
    case 'event_invite':
    case 'event_reminder':
      return CalendarIcon
    case 'order_shipped':
    case 'order_delivered':
      return PackageIcon
    case 'price_drop':
      return TrendingDownIcon
    case 'wishlist_update':
      return HeartIcon
    case 'review_posted':
      return StarIcon
    case 'system':
      return InfoIcon
    default:
      return MessageSquareIcon
  }
}

export const readerNotifications: Notification[] = [
  {
    id: 'r1',
    type: 'order_shipped',
    category: 'orders',
    title: 'Order Shipped',
    message: 'Your order #12345 containing "The Silent Echo" has been shipped.',
    time: '2 hours ago',
    read: false,
    actionable: false,
    relatedLink: '/orders/12345',
  },
  {
    id: 'r2',
    type: 'new_release',
    category: 'releases',
    title: 'New Release Alert',
    message: 'Elena Richards released a new book: "Broken Echoes".',
    time: '1 day ago',
    read: false,
    actionable: false,
    senderName: 'Elena Richards',
    senderAvatar:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
    relatedImage:
      'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=100',
    relatedLink: '/book/broken-echoes',
  },
  {
    id: 'r3',
    type: 'price_drop',
    category: 'system',
    title: 'Price Drop on Wishlist',
    message: '"Midnight Shadows" is now on sale for $2.99 (was $5.99).',
    time: '2 days ago',
    read: true,
    actionable: false,
    relatedImage:
      'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?w=100',
    relatedLink: '/book/midnight-shadows',
  },
  {
    id: 'r4',
    type: 'new_follower',
    category: 'social',
    title: 'New Follower',
    message: 'Sarah Johnson started following your reading list.',
    time: '3 days ago',
    read: true,
    actionable: false,
    senderName: 'Sarah Johnson',
    senderAvatar:
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
    relatedLink: '/profile/sarah-j',
  },
  {
    id: 'r5',
    type: 'event_reminder',
    category: 'events',
    title: 'Event Reminder',
    message: 'Book signing with Marcus Chen is tomorrow at 3:00 PM.',
    time: '4 days ago',
    read: true,
    actionable: false,
    relatedLink: '/events/marcus-chen-signing',
  },
  {
    id: 'r6',
    type: 'review_posted',
    category: 'social',
    title: 'Review Reply',
    message: 'Marcus Chen replied to your review of "Beyond the Veil".',
    time: '1 week ago',
    read: true,
    actionable: false,
    senderName: 'Marcus Chen',
    senderAvatar:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
    relatedLink: '/book/beyond-the-veil#reviews',
  },
  {
    id: 'r7',
    type: 'system',
    category: 'system',
    title: 'Welcome to LitLaunch!',
    message: 'Complete your profile to get personalized book recommendations.',
    time: '2 weeks ago',
    read: true,
    actionable: false,
    relatedLink: '/profile/settings',
  },
]

export const vendorNotifications: Notification[] = [
  {
    id: 'v1',
    type: 'co_author_request',
    category: 'approvals',
    title: 'Co-Author Request',
    message:
      'Marcus Chen added you as a co-author on "Beyond the Veil". Approve to add this book to your profile.',
    time: '1 hour ago',
    read: false,
    actionable: true,
    actions: {
      approve: 'Approve',
      decline: 'Decline',
    },
    senderName: 'Marcus Chen',
    senderAvatar:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
    relatedImage:
      'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=100',
    relatedLink: '/book/beyond-the-veil',
  },
  {
    id: 'v2',
    type: 'arc_application',
    category: 'approvals',
    title: 'New ARC Applications',
    message: 'You have 15 new ARC reader applications for "Midnight Secrets".',
    time: '3 hours ago',
    read: false,
    actionable: false,
    relatedLink: '/vendors/arc/midnight-secrets',
  },
  {
    id: 'v3',
    type: 'collaboration_invite',
    category: 'approvals',
    title: 'Collaboration Invite',
    message:
      'Luna Design Co. wants to collaborate on cover art for your upcoming release.',
    time: '5 hours ago',
    read: false,
    actionable: true,
    actions: {
      approve: 'Accept',
      decline: 'Decline',
    },
    senderName: 'Luna Design Co.',
    senderAvatar:
      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
    relatedLink: '/vendors/collaborations/luna-design',
  },
  {
    id: 'v4',
    type: 'bookstore_stock',
    category: 'approvals',
    title: 'Bookstore Stock Request',
    message: 'Indie Reads Bookshop wants to stock "The Silent Echo".',
    time: '1 day ago',
    read: true,
    actionable: true,
    actions: {
      approve: 'Approve',
      decline: 'Decline',
    },
    senderName: 'Indie Reads Bookshop',
    relatedLink: '/vendors/bookstores/requests',
  },
  {
    id: 'v5',
    type: 'event_invite',
    category: 'events',
    title: 'Event Invitation',
    message: 'You have been invited to speak at Romance Con 2024.',
    time: '2 days ago',
    read: true,
    actionable: true,
    actions: {
      approve: 'Accept',
      decline: 'Decline',
    },
    relatedLink: '/events/romance-con-2024',
  },
  {
    id: 'v6',
    type: 'review_posted',
    category: 'social',
    title: 'New Review',
    message: 'A new 5-star review was posted for "The Silent Echo".',
    time: '3 days ago',
    read: true,
    actionable: false,
    relatedLink: '/book/the-silent-echo#reviews',
  },
  {
    id: 'v7',
    type: 'system',
    category: 'system',
    title: 'Royalty Payment Processed',
    message: 'Your monthly royalty payment of $1,245.50 has been processed.',
    time: '1 week ago',
    read: true,
    actionable: false,
    relatedLink: '/vendors/royalties',
  },
]
